import React from "react";
import { Store, ShieldCheck, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../app/context/AuthContext";
import { useNavigate } from "react-router-dom";

export function LoginScreen() {
  const { availableUsers, switchUser } = useAuth();
  const navigate = useNavigate();

  const handleSelectRole = (userId) => {
    switchUser(userId);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden text-slate-100 selection:bg-emerald-500">
      {/* Background Glow */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-xl space-y-8">
        {/* Brand Header */}
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-400 text-white shadow-2xl shadow-emerald-500/30">
            <Store className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight font-display text-white">
            Kirana Store Inventory
          </h1>
          <p className="text-xs font-mono text-emerald-400 font-semibold tracking-wider">
            One system. Every product. Every location. Real-time.
          </p>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            Enterprise multi-warehouse procurement, exact bin putaway, batch expiry radar, and dual retail/wholesale sales platform.
          </p>
        </div>

        {/* Role-Based Quick Login Card */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/80 backdrop-blur-xl p-6 sm:p-8 shadow-2xl space-y-5">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Role-Based Access Control (RBAC)
            </span>
            <h3 className="text-base font-bold text-white font-display mt-0.5">
              Select Enterprise Role to Enter System
            </h3>
            <p className="text-xs text-slate-400">
              Each user sees role-tailored sidebars, KPI dashboards, and permissions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {availableUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelectRole(user.id)}
                className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800 hover:border-emerald-500 hover:bg-slate-800/80 transition-all duration-200 text-left flex items-center justify-between group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${user.color || "from-emerald-600 to-teal-500"} text-white flex items-center justify-center font-bold text-sm shadow-md ring-2 ring-emerald-500/20 group-hover:ring-emerald-500 transition`}>
                    {user.initials}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-white group-hover:text-emerald-400 transition font-display">
                      {user.name}
                    </h4>
                    <span className="text-[10px] text-slate-400 block mt-0.5">
                      {user.role}
                    </span>
                  </div>
                </div>

                <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 group-hover:translate-x-1 transition" />
              </button>
            ))}
          </div>
        </div>

        {/* Footer info */}
        <div className="text-center text-[11px] text-slate-500 font-mono">
          <p>Kirana Multi-Warehouse SaaS • Production Ready</p>
        </div>
      </div>
    </div>
  );
}
